import { useCreateSimpleAssessment } from "@/api/console/simple-assessments/useCreateSimpleAssessment";
import { useGetLatestSimpleAssessment } from "@/api/console/simple-assessments/useGetLatestSimpleAssessment";
import Category from "@/components/Assessments/Category";
import CategoryPreview from "@/components/Assessments/CategoryPreview";
import ReorderCategoryDialog from "@/components/Assessments/ReorderCategoryDialog";
import LoadingScreen from "@/components/Loading";
import { Button } from "@/components/ui/button";
import toast from "@/lib/toast";
import {
  FormValues,
  formValuesSchema,
} from "@/schema/console/assessments/create/FormValues";
import { SimpleAssessmentCreate } from "@/schema/console/simple-assessments/SimpleAssessmentDetail";
import Heading from "@/src/console/(auth)/(manage-assessments)/simple-assessments/create/_components/Heading";
import { AddRegular } from "@fluentui/react-icons";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import {
  FormProvider,
  useFieldArray,
  useForm,
  useWatch,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { v1 as uuid } from "uuid";

function CreateSimpleAssessment() {
  const { t } = useTranslation();
  const method = useForm<FormValues>({
    resolver: zodResolver(formValuesSchema),
    defaultValues: {
      selectedCategory: "",
      categories: [],
    },
  });
  const { control, handleSubmit } = method;

  const [searchParams] = useSearchParams();
  const duplicate = searchParams.get("duplicate") === "true";

  const { mutate, isLoading } = useGetLatestSimpleAssessment({
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnMount: false,
    revalidateOnReconnect: false,
    onSuccess: (data) => {
      const categories = data.data.main_categories.map(
        ({ sub_categories: sc, content }) => {
          const subcategories = sc.map(({ questions: q, content }) => {
            const questions = q.map(({ content, points }) => ({
              content,
              id: uuid(),
              type: "simple" as const,
              isEdit: false,
              perfectScore: points,
            }));
            return {
              id: uuid(),
              content,
              isEdit: false,
              questions,
            };
          });
          return {
            id: uuid(),
            content,
            isEdit: false,
            selectedSubCategory: subcategories[subcategories.length - 1].id,
            subcategories,
          };
        },
      );
      method.reset({
        selectedCategory: categories
          ? categories[categories.length - 1].id
          : "",
        categories,
      });
    },
  });

  const { trigger: saveDraft, isMutating: isSavingDraft } =
    useCreateSimpleAssessment();

  const { trigger: publish, isMutating: isPublishing } =
    useCreateSimpleAssessment();

  useEffect(() => {
    if (!duplicate) return;
    mutate();
  }, [duplicate]);

  const {
    fields: categoryFields,
    append,
    remove,
    replace,
  } = useFieldArray({
    control,
    name: "categories",
    keyName: "key",
  });

  const selectedCategory = useWatch({
    control,
    name: "selectedCategory",
  });

  const prepareData = (
    data: FormValues,
    isPublished: boolean,
  ): SimpleAssessmentCreate => {
    const main_categories = data.categories.map(
      ({ content, subcategories }) => {
        const sub_categories = subcategories.map(
          ({ content, questions: q }) => {
            const questions = q.map(({ content, perfectScore }) => ({
              content,
              points: perfectScore,
            }));
            return {
              content,
              questions,
            };
          },
        );
        return {
          content,
          sub_categories,
        };
      },
    );
    return {
      is_premium: false,
      is_published: isPublished,
      main_categories,
    };
  };

  const onSaveDraft = () => {
    handleSubmit(
      (data) => {
        toast.loading(t("save-draft-loading"));
        saveDraft(prepareData(data, false));
      },
      (errors) => {
        if (errors) {
          toast.error(JSON.stringify(errors));
        }
      },
    )();
  };

  const onPublish = () => {
    handleSubmit(
      (data) => {
        toast.loading(t("publish-loading"));
        publish(prepareData(data, true));
      },
      (errors) => {
        if (errors) {
          toast.error(JSON.stringify(errors));
        }
      },
    )();
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <FormProvider {...method}>
      <Heading
        isPublishing={isPublishing}
        isSavingDraft={isSavingDraft}
        onSaveDraft={onSaveDraft}
        onPublish={onPublish}
      />
      <div className="flex min-h-[calc(100%-168px)]">
        {/* Category list */}
        <div className="w-[300px] min-w-[300px] border-r bg-white p-4">
          <div className="mb-2 ml-3 flex items-start justify-between gap-1">
            <div className="text-lg font-bold">{t("categories")}</div>
            <div className="flex gap-2">
              <ReorderCategoryDialog
                onReorderFinish={(categories) => {
                  replace(categories);
                }}
              />
              <Button
                onClick={() => {
                  append({
                    id: uuid(),
                    content: "",
                    isEdit: true,
                    selectedSubCategory: "",
                    subcategories: [],
                  });
                }}
                variant="default"
                size="icon"
              >
                <AddRegular fontSize={20} />
              </Button>
            </div>
          </div>
          {categoryFields.map((category, categoryIndex) => (
            <CategoryPreview
              key={category.id}
              index={categoryIndex}
              onRemove={() => {
                remove(categoryIndex);
              }}
            />
          ))}
        </div>
        <div className="grow">
          {categoryFields.map((category, index) => {
            return (
              category.id === selectedCategory && (
                <Category type="simple" key={category.id} index={index} />
              )
            );
          })}
        </div>
      </div>
    </FormProvider>
  );
}

export default CreateSimpleAssessment;
